import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useApolloClient } from '@apollo/client';
// Api
import { GET_STREAM_REDUCED } from 'api/streams/queries';
// Types
import {
  GetStreamReduced,
  GetStreamReducedVariables,
} from 'api/streams/types/GetStreamReduced';
// Hooks
import { useAppContext, useGetCurrUser } from 'hooks';
import { useGuestPpvBuyFlow } from 'hooks/useGuestPpvBuyFlow';
// Helpers
import { getStreamNavigationPath } from 'helpers/streamNavigation';
import { getPpvBuyStreamIdFromLocalStorage } from 'helpers/storage';

const PpvBuyPostAuthWatcher = (): null => {
  const { push, pathname } = useRouter();
  const apolloClient = useApolloClient();
  const { data: userData } = useGetCurrUser();
  const { token } = useAppContext();
  const { completeGuestPpvBuy } = useGuestPpvBuyFlow();
  const isRedirecting = useRef(false);

  const loggedUserId = userData?.me?.id || null;
  const isLoggedIn = Boolean(loggedUserId && token?.accessToken);

  useEffect(() => {
    const streamId = getPpvBuyStreamIdFromLocalStorage();

    if (!streamId || !isLoggedIn || isRedirecting.current) {
      return;
    }

    let cancelled = false;

    const redirectToStreamDestination = async () => {
      isRedirecting.current = true;

      try {
        const { data } = await apolloClient.query<
          GetStreamReduced,
          GetStreamReducedVariables
        >({
          query: GET_STREAM_REDUCED,
          variables: {
            input: { id: streamId },
          },
          fetchPolicy: 'network-only',
        });

        if (cancelled) {
          return;
        }

        if (!data?.stream) {
          completeGuestPpvBuy();
          return;
        }

        const destination = getStreamNavigationPath(
          data.stream,
          loggedUserId,
          true
        );

        completeGuestPpvBuy();

        if (pathname !== destination) {
          await push(destination);
        }
      } catch (error) {
        console.error(error);
        completeGuestPpvBuy();
      } finally {
        isRedirecting.current = false;
      }
    };

    redirectToStreamDestination();

    return () => {
      cancelled = true;
    };
  }, [
    apolloClient,
    completeGuestPpvBuy,
    isLoggedIn,
    loggedUserId,
    pathname,
    push,
    token?.accessToken,
  ]);

  return null;
};

export default PpvBuyPostAuthWatcher;
